import { TableBadgeLabel, Translate } from '@components'

import { Flex, Icon, SearchInput, Space, Spacing, Table, TableColumn } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'react-table'

import { TableIds } from '../../../../../../enums/tableIds'
import { OrganizationsTableAccessor } from '../../enums/OrganizationsTableAccessor'
import { useOrganizations } from '../../hooks/useOrganizations'
import { useOrganizationsTableData } from '../../hooks/useOrganizationsTableData'
import { OrganizationsTableRow } from '../../types/OrganizationsTableRow'
import * as Styled from './styles'

const columns: TableColumn<OrganizationsTableRow>[] = [
  {
    accessor: OrganizationsTableAccessor.Icon,
    Cell: ({ value: icon }) => (icon ? <Styled.IconWrapper src={icon} /> : <Icon size="lg" icon="briefcaseBusiness" />),
    alignment: 'left',
    size: 'xxxxs',
    sortable: false,
  },
  {
    Header: <Translate value="settings.organization.table.name" />,
    accessor: OrganizationsTableAccessor.Name,
    Cell: ({ value: name }) => name,
    alignment: 'left',
    size: 'xxxl',
    sortable: false,
  },
  {
    Header: <Translate value="settings.organization.table.tax_id" />,
    accessor: OrganizationsTableAccessor.TaxId,
    Cell: ({ value: taxId }) => taxId,
    alignment: 'left',
    size: 'xxxs',
    sortable: false,
  },
]

export const OrganizationsTable = (): ReactElement => {
  const [search, setSearch] = useState<undefined | string>()

  const { organizations, isLoading } = useOrganizations()
  const tableData = useOrganizationsTableData(organizations)
  const { t } = useTranslation()

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value || undefined)
    },
    [setSearch],
  )

  const handleClear = useCallback(() => {
    setSearch(undefined)
  }, [setSearch])

  const results = useMemo(() => {
    return search
      ? tableData.filter((organization) => organization.name.toLowerCase().includes(search.toLowerCase()))
      : tableData
  }, [search, tableData])

  const onRowClick = useCallback((item: Row<OrganizationsTableRow>) => {
    window.location.href = `/${item.original?.url}`
  }, [])

  return (
    <Flex gap={Spacing.S16} flexDirection="column">
      <Flex alignItems="center">
        <TableBadgeLabel amount={results.length} isLoading={isLoading}>
          {t('organizations.all_organizations')}
        </TableBadgeLabel>
        <SearchInput onChange={handleChange} value={search} onClear={handleClear} placeholder={`${t('search')}...`} />
      </Flex>
      <Table
        columns={columns}
        data={results}
        id={TableIds.Organizations}
        isLoading={isLoading}
        itemsPerPage={undefined}
        withPagination={false}
        withStickyHeader
        onRowClick={onRowClick}
        withColumnsFiltering={false}
      />
      <Space size="s32" />
    </Flex>
  )
}
